import { useState, useRef } from 'react'
import { api } from '../utils/api'
import { t } from '../i18n/index'
import type { Video } from '../types'

export default function ImportPage() {
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<{ imported: number; skipped: number; duplicates: number } | null>(null)
  const [videos, setVideos] = useState<Video[]>([])
  const [dragOver, setDragOver] = useState(false)
  const hiddenInput = useRef<HTMLInputElement>(null)

  const scanFromFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setLoading(true)
    try {
      const res = await api.importFiles(Array.from(files)) as {
        imported: number; skipped: number; duplicates: number; videos: Video[]
      }
      setResult({ imported: res.imported, skipped: res.skipped, duplicates: res.duplicates })
      setVideos(prev => [...prev, ...res.videos.filter(v => !prev.some(p => p.id === v.id))])
    } catch (e) {
      alert(`${t('import.drop')} ${e}`)
    } finally {
      setLoading(false)
    }
  }

  const handleFolderClick = () => {
    if ('showDirectoryPicker' in window) {
      (window as any).showDirectoryPicker({ mode: 'read' }).then(async (dir: any) => {
        const files: File[] = []
        for await (const entry of dir.values()) {
          if (entry.kind === 'file') {
            const ext = entry.name.split('.').pop()?.toLowerCase()
            if (['mp4', 'mov', 'm4v', 'ts'].includes(ext || '')) {
              files.push(await entry.getFile())
            }
          }
        }
        if (files.length > 0) await scanFromFiles(files as any)
      }).catch(() => {})
    } else {
      hiddenInput.current?.click()
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    scanFromFiles(e.target.files)
    e.target.value = ''
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const files = Array.from(e.dataTransfer.files).filter(f => /\.(mp4|mov|m4v|ts)$/i.test(f.name))
    if (files.length > 0) await scanFromFiles(files as any)
  }

  return (
    <div className="max-w-5xl">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label: 'VIDEOS', value: videos.length.toString(), suffix: '个' },
          { label: 'STORAGE', value: '4.2', suffix: 'GB' },
          { label: 'EXPORTS', value: '7', suffix: '个' },
        ].map(({ label, value, suffix }) => (
          <div key={label} className="cyber-card p-4">
            <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">{label}</div>
            <div className="font-mono text-2xl font-black text-gray-900">{value}<span className="text-sm text-gray-400 font-normal ml-1">{suffix}</span></div>
          </div>
        ))}
      </div>

      {/* Drop zone */}
      <div className="cyber-card mb-6">
        <div
          className={`p-10 text-center transition-all duration-100 cursor-pointer ${
            dragOver ? 'border-cyber-primary bg-cyber-primary/5 ring-1 ring-cyber-primary/30' : 'border-dashed border-gray-300 hover:border-cyber-primary'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={handleFolderClick}
        >
          <div className="w-14 h-14 mx-auto mb-4 bg-gray-50 border border-gray-200 rounded-sm flex items-center justify-center text-2xl">📂</div>
          <div className="font-mono text-sm font-bold uppercase tracking-widest text-gray-900 mb-1">{t('import.drop')}</div>
          <div className="font-mono text-xs text-gray-400 mb-5">{t('import.drop-desc')}</div>
          <div className="flex gap-3 justify-center">
            <button onClick={(e) => { e.stopPropagation(); handleFolderClick() }} disabled={loading} className="cyber-btn-primary">
              {loading ? '⏳ PROCESSING...' : `◈ ${t('import.open-folder')}`}
            </button>
            <button onClick={(e) => { e.stopPropagation(); hiddenInput.current?.click() }} disabled={loading} className="cyber-btn-outline">
              ◇ {t('import.select-files')}
            </button>
            <input ref={hiddenInput} type="file" multiple accept=".mp4,.mov,.m4v,.ts" className="hidden" onChange={handleInputChange} />
          </div>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className="cyber-card mb-6">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <span className="font-mono text-xs font-bold uppercase tracking-widest text-gray-500">{t('import.recent')}</span>
            <span className="font-mono text-xs text-gray-400">{new Date().toLocaleTimeString()}</span>
          </div>
          <div className="p-5">
            <div className="flex gap-8">
              {[
                { label: t('import.imported'), value: result.imported, color: 'text-teal-600', bg: 'bg-cyber-primary' },
                { label: t('import.skipped'), value: result.skipped, color: 'text-yellow-600', bg: 'bg-yellow-500' },
                { label: t('import.duplicates'), value: result.duplicates, color: 'text-gray-500', bg: 'bg-gray-400' },
              ].map(({ label, value, color, bg }) => (
                <div key={label} className="flex items-center gap-3">
                  <div className={`w-2 h-8 ${bg} rounded-sm`} />
                  <div>
                    <div className={`font-mono text-lg font-black ${color}`}>{value}</div>
                    <div className="font-mono text-[10px] text-gray-400 uppercase tracking-wider">{label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Video list */}
      {videos.length > 0 && (
        <div className="cyber-card">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <span className="font-mono text-xs font-bold uppercase tracking-widest text-gray-500">{t('import.recent')} ({videos.length})</span>
            <button onClick={() => { setVideos([]); setResult(null) }} className="font-mono text-[10px] text-gray-400 hover:text-gray-600 uppercase tracking-wider transition-colors">[{t('import.clear')}]</button>
          </div>
          <div className="divide-y divide-gray-100">
            {videos.map((v) => (
              <div key={v.id} className="flex items-center gap-4 px-5 py-3 hover:bg-cyber-primary/3 transition-colors cursor-pointer">
                <div className="w-16 h-9 bg-gray-50 border border-gray-200 rounded-sm flex items-center justify-center text-base flex-shrink-0">🎬</div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm font-bold text-gray-900 truncate">{v.filename}</div>
                  <div className="font-mono text-[11px] text-gray-400 mt-0.5 flex gap-3">
                    {v.camera_angle && <span className="px-1.5 py-0.5 bg-accent-dim text-accent rounded-sm text-[10px] font-bold">{v.camera_angle.toUpperCase()}</span>}
                    <span>{v.recorded_at ? new Date(v.recorded_at).toLocaleString('zh-CN') : '--'}</span>
                  </div>
                </div>
                <div className="font-mono text-xs text-gray-400 text-right flex-shrink-0">
                  {v.file_size && <div className="font-bold text-gray-600">{(v.file_size / 1024 / 1024).toFixed(1)} MB</div>}
                  {v.duration && <div>{Math.floor(v.duration / 60)}:{String(Math.floor(v.duration % 60)).padStart(2, '0')}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
